/**
 * Pick the right React 18 entry point for the HTML we were served.
 *
 * The marketing routes are prerendered into static HTML at build time
 * (scripts/prerender.js) so non-JS crawlers can read them. `createRoot().render()`
 * ignores whatever markup is already in the container and rebuilds it from
 * scratch, which throws away the prerendered DOM and flashes the page. React's
 * answer to pre-existing server markup is `hydrateRoot`, which adopts it.
 *
 * The app routes (/studio and friends) are NOT prerendered, so they are still
 * served the empty shell and must use `createRoot`. Hydrating an empty
 * container would work but log a mismatch for every node.
 *
 * `firstElementChild`, not `hasChildNodes()`: a stray whitespace text node
 * inside `<div id="root">` would make hasChildNodes() true for an empty shell
 * and send us down the hydrate path with nothing to hydrate.
 *
 * @param {Element} container            the #root element
 * @param {React.ReactNode} element      the app tree
 * @param {{hydrateRoot: Function, createRoot: Function}} reactDomClient
 * @returns {{root: any, hydrated: boolean}}
 */
export default function mountApp(container, element, { hydrateRoot, createRoot }) {
  if (container.firstElementChild) {
    return { root: hydrateRoot(container, element), hydrated: true };
  }
  const root = createRoot(container);
  root.render(element);
  return { root, hydrated: false };
}
